import { Plus, Copy, Eye, Check, Edit2, Trash2, Phone, MapPin, PackageOpen, Truck, Box } from 'lucide-react';

// Format price with comma as decimal separator
const formatPrice = (value) => {
    if (value === undefined || value === null) return '0,00';
    return value.toFixed(2).replace('.', ',');
};

export const CustomerCard = ({ 
    customer, 
    index, 
    onView, 
    onCopy, 
    onComplete, 
    onDelete, 
    onEdit, 
    onAdd,
    onAddFromPalet,
    onAddTransportFee,
    onToggleInformed,
    onSetLocationBox,
    onRemoveLocationBox
}) => {
    const isInformed = customer.informed;
    // Check if customer already has transport fee
    const hasTransportFee = customer.items?.some(item => item.barcode === 'TAXA_TRANSPORT' || item.is_fee);
    
    // Determine customer status for background color
    const hasPhone = customer.phone && customer.phone.trim();
    const hasName = customer.name && customer.name.trim();
    const hasAddress = customer.address && customer.address.trim();
    
    // Background color logic:
    // - Green: has phone AND (has name OR has address) = client complet
    // - Blue: has phone only (no name, no address) = client doar cu telefon
    // - Red: no phone = client necunoscut
    let cardBgClass = 'bg-white'; // default
    if (!hasPhone) {
        cardBgClass = 'bg-red-50 border-red-200'; // No phone - RED
    } else if (!hasName && !hasAddress) {
        cardBgClass = 'bg-blue-50 border-blue-200'; // Phone only - BLUE
    } else {
        cardBgClass = 'bg-emerald-50 border-emerald-200'; // Complete - GREEN
    }
    
    return (
        <div
            className={`${cardBgClass} border rounded-xl p-3 hover:shadow-sm transition-shadow`}
            data-testid={`customer-card-${customer.customer_id}`}
        >
            {/* Desktop Layout */}
            <div className="hidden sm:flex items-center justify-between gap-2">
                {/* Left: Nr + ID + Details */}
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-xs font-bold text-zinc-400">#{index + 1}</span>
                    <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                        <span className="font-bold text-violet-600 text-sm">{customer.customer_id}</span>
                    </div>
                    {/* Last box indicator */}
                    {customer.last_box && !customer.location_box && (
                        <div className="px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-center gap-1" title={`Fost în cutia ${customer.last_box}`}>
                            <Box size={12} />
                            <span className="font-medium">Cutia {customer.last_box}</span>
                        </div>
                    )}
                    {/* Current location box (L-type) */}
                    {customer.location_box && (
                        <button
                            onClick={() => onSetLocationBox?.(customer.customer_id, customer.location_box)}
                            className="px-2 py-1 bg-purple-100 border border-purple-300 rounded text-xs text-purple-700 flex items-center gap-1 hover:bg-purple-200 transition-colors"
                            title={`Click pentru a schimba localizarea`}
                        >
                            <Box size={12} />
                            <span className="font-bold">{customer.location_box}</span>
                        </button>
                    )}
                    {/* Details + Count side by side */}
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        {/* Name, Phone, Address */}
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                {customer.name && (
                                    <p className="font-medium text-zinc-900 text-sm truncate">{customer.name}</p>
                                )}
                                {customer.phone && (
                                    <p className="text-xs text-zinc-500 flex items-center gap-0.5">
                                        <Phone size={10} />
                                        {customer.phone}
                                    </p>
                                )}
                            </div>
                            {customer.address && (
                                <p className="text-xs text-zinc-400 truncate flex items-center gap-0.5">
                                    <MapPin size={10} />
                                    {customer.address}
                                </p>
                            )}
                        </div>
                        {/* Product count */}
                        <div className="text-center px-2">
                            <p className="text-lg font-bold text-emerald-600">{customer.total_products || customer.item_count}</p>
                            <p className="text-xs text-zinc-400">prod.</p>
                        </div>
                    </div>
                </div>
                
                {/* Right: Buttons + Price */}
                <div className="flex items-center gap-1.5">
                    <button onClick={onAdd} className="p-2 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Adaugă din Magazin">
                        <Plus size={28} />
                    </button>
                    {onAddFromPalet && (
                        <button onClick={onAddFromPalet} className="p-2 text-zinc-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg" title="Adaugă din Palet">
                            <PackageOpen size={28} />
                        </button>
                    )}
                    {!hasTransportFee && onAddTransportFee && (
                        <button onClick={onAddTransportFee} className="p-2 text-zinc-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg" title="Adaugă Taxa Transport">
                            <Truck size={28} />
                        </button>
                    )}
                    <button onClick={onCopy} className="p-2 text-zinc-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Copiază">
                        <Copy size={28} />
                    </button>
                    <button onClick={onView} className="p-2 text-zinc-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg" title="Vezi" data-testid={`view-order-${customer.customer_id}`}>
                        <Eye size={28} />
                    </button>
                    <button 
                        onClick={onToggleInformed} 
                        className={`p-2 rounded-lg ${isInformed ? 'text-green-500 bg-green-50' : 'text-zinc-300 hover:text-green-500 hover:bg-green-50'}`} 
                        title={isInformed ? 'Client informat ✓' : 'Marchează ca informat'}
                    >
                        <Phone size={28} />
                    </button>
                    {/* Location box button */}
                    <button 
                        onClick={() => onSetLocationBox?.(customer.customer_id, customer.location_box || '')}
                        className={`p-2 rounded-lg ${customer.location_box ? 'text-purple-500 bg-purple-50' : 'text-zinc-300 hover:text-purple-500 hover:bg-purple-50'}`} 
                        title={customer.location_box ? `Localizare: ${customer.location_box}` : 'Setează localizare (L1, L2...)'}
                    >
                        <Box size={28} />
                    </button>
                    <button onClick={onComplete} className="p-2 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Finalizează">
                        <Check size={28} />
                    </button>
                    <button onClick={onEdit} className="p-2 text-zinc-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Editează">
                        <Edit2 size={28} />
                    </button>
                    <button onClick={onDelete} className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Șterge">
                        <Trash2 size={28} />
                    </button>
                    
                    {/* Price */}
                    <div className="text-right ml-2 pl-2 border-l border-zinc-200">
                        <p className="text-lg font-bold text-violet-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {formatPrice(customer.order_total)}
                        </p>
                        <p className="text-xs text-zinc-400">RON</p>
                    </div>
                </div>
            </div>

            {/* Mobile Layout */}
            <div className="sm:hidden">
                {/* Top row: ID + Details + Price */}
                <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-xs font-bold text-zinc-400">#{index + 1}</span>
                        <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                            <span className="font-bold text-violet-600 text-sm">{customer.customer_id}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                            {customer.name && (
                                <p className="font-medium text-zinc-900 text-sm truncate">{customer.name}</p>
                            )}
                            {customer.phone && (
                                <p className="text-xs text-zinc-500 flex items-center gap-0.5">
                                    <Phone size={10} />
                                    {customer.phone}
                                </p>
                            )}
                            {customer.address && (
                                <p className="text-xs text-zinc-400 truncate flex items-center gap-0.5">
                                    <MapPin size={10} />
                                    {customer.address}
                                </p>
                            )}
                            {/* Last box indicator - mobile */}
                            {customer.last_box && (
                                <p className="text-xs text-blue-600 flex items-center gap-0.5 mt-0.5">
                                    <Box size={10} />
                                    Cutia {customer.last_box}
                                </p>
                            )}
                        </div>
                    </div>
                    {/* Price + Count */}
                    <div className="text-right shrink-0">
                        <p className="text-xl font-bold text-violet-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {formatPrice(customer.order_total)}
                        </p>
                        <p className="text-xs text-zinc-400">{customer.total_products || customer.item_count} prod.</p>
                    </div>
                </div>
                
                {/* Bottom row: Action buttons */}
                <div className="flex items-center justify-between gap-1 pt-2 border-t border-zinc-100">
                    <button onClick={onAdd} className="flex-1 p-2.5 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg flex items-center justify-center" title="Adaugă din Magazin">
                        <Plus size={24} />
                    </button>
                    {onAddFromPalet && (
                        <button onClick={onAddFromPalet} className="flex-1 p-2.5 text-zinc-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg flex items-center justify-center" title="Adaugă din Palet">
                            <PackageOpen size={24} />
                        </button>
                    )}
                    {!hasTransportFee && onAddTransportFee && (
                        <button onClick={onAddTransportFee} className="flex-1 p-2.5 text-zinc-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg flex items-center justify-center" title="Adaugă Taxa Transport">
                            <Truck size={24} />
                        </button>
                    )}
                    <button onClick={onCopy} className="flex-1 p-2.5 text-zinc-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg flex items-center justify-center" title="Copiază">
                        <Copy size={24} />
                    </button>
                    <button onClick={onView} className="flex-1 p-2.5 text-zinc-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg flex items-center justify-center" title="Vezi" data-testid={`view-order-mobile-${customer.customer_id}`}>
                        <Eye size={24} />
                    </button>
                    <button 
                        onClick={onToggleInformed} 
                        className={`flex-1 p-2.5 rounded-lg flex items-center justify-center ${isInformed ? 'text-green-500 bg-green-50' : 'text-zinc-300 hover:text-green-500 hover:bg-green-50'}`} 
                        title={isInformed ? 'Informat ✓' : 'Informează'}
                    >
                        <Phone size={24} />
                    </button>
                    <button onClick={onComplete} className="flex-1 p-2.5 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg flex items-center justify-center" title="Finalizează">
                        <Check size={24} />
                    </button>
                    <button onClick={onEdit} className="flex-1 p-2.5 text-zinc-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg flex items-center justify-center" title="Editează">
                        <Edit2 size={24} />
                    </button>
                    <button onClick={onDelete} className="flex-1 p-2.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex items-center justify-center" title="Șterge">
                        <Trash2 size={24} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CustomerCard;
